import {
  GetHeaderResponse,
  GetHeaderResponse_Brand,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  FullWidthImage,
  SPACING,
  Z_INDEXES,
  desktopAndUpLinaria,
  gridCols,
  mediaPortrait,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {DESKTOP_STICKY_HEIGHT} from '../../Header/constants';

export interface StoryHeaderViewProps {
  HeaderElements: React.ComponentType<{
    id: GetHeaderResponse_Brand['id'];
    brandName: GetHeaderResponse_Brand['brandName'];
    promotionTracker: GetHeaderResponse_Brand['promotionTracker'];
  }>;
}

export const StoryHeaderView: React.FC<
  GetHeaderResponse & StoryHeaderViewProps
> = ({HeaderElements, headline, subline, image, brand}) => {
  return (
    <Wrapper>
      <StyledFullWidthImage
        dataTestId="storyHeaderImage"
        imageUrl={image.url}
        description={image.description}
        aspectRatio="2:1"
        highPrioImage
      />

      {brand && (
        <TopWrapper>
          <HeaderElements
            id={brand.id}
            brandName={brand.brandName}
            promotionTracker={brand.promotionTracker}
          />
        </TopWrapper>
      )}

      <BottomWrapper>
        {brand?.logo && (
          <LogoWrapper>
            <BrandLogo
              name={brand.brandName}
              image={brand.logo}
              maxWidth="100%"
              maxHeight="100%"
            />
          </LogoWrapper>
        )}
        {subline && <Subline>{subline}</Subline>}
        {headline && !brand?.logo && <Headline>{headline}</Headline>}
      </BottomWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
`;

const StyledFullWidthImage = styled(FullWidthImage)`
  max-height: calc(100vh - ${DESKTOP_STICKY_HEIGHT}px);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      180deg,
      color-mix(in srgb, ${COLORS.Black} 0%, transparent) 0%,
      color-mix(in srgb, ${COLORS.Black} 30%, transparent) 100%
    );
  }

  img {
    aspect-ratio: 2 / 1;
  }

  @media ${mediaPortrait} {
    img {
      aspect-ratio: 3 / 4;
    }
  }
`;

const BottomWrapper = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  gap: ${SPACING['Y.Sm']};
  flex-direction: column;
  align-items: center;
  padding: ${SPACING['X.None']} ${SPACING['X.Lg']} ${SPACING['X.Xl']}
    ${SPACING['X.Lg']};

  ${tabletAndUpLinaria(`
    gap: ${SPACING['Y.Lg']};
    top: 0;
    justify-content: center;
    padding-bottom: 0;
  `)}
`;

const Headline = styled.h1`
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.w700};
  color: ${COLORS.White};
  margin: 0;
  text-align: center;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px48};
  `)}
`;

const Subline = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w500};
  color: ${COLORS.White};
  text-transform: uppercase;
  letter-spacing: 0.56px;
  margin: 0;
`;

const LogoWrapper = styled.div`
  ${gridCols(6)}
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${tabletAndUpLinaria(`
    height: 150px
  `)}
`;

const TopWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  width: calc(100% - 26px);
  top: 9px;
  z-index: ${Z_INDEXES.TADARIDA_HEADER_NAVIGATION_BUTTONS};

  ${tabletAndUpLinaria(`
    width: calc(100% - 30px);
    top: 12px;
  `)}

  ${desktopAndUpLinaria(`
    width: calc(100% - 120px);
    top: 32px;
  `)}
`;
